import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { GetParticipants, GetDomain, PostRegistration, GetSessions } from '../../service/SessionAPI'; // Import your API methods
import { AddMemberModal } from './AddMember';
import Loader from '../../StaticComponent/Loader';
import { ConfirmationPopup } from './ConfirmationPopup';
import { SessionRegistration } from './SessionRegistration';
import { Toast, Toaster } from './Toast';
import EditParticipants from './EditParticipant';
import { encryptStorage } from '../../constant';

export const SessionParticipants = () => {
    const { sessionId, slotId } = useParams(); // Get sessionId and slotId from URL params
    const [participants, setParticipants] = useState({
        session_id: '',
        session_name: '',
        session_date: '',
        start_time: '',
        end_time: '',
        participants: [],
        total_count: 0
    });
    const [sessionInfo, setSessionInfo] = useState({
        seatAvailable: 0,
        registeredCount: 0,
        sessionGrade: ''
    });
    const [domains, setDomains] = useState([]); // State for domains
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showAddMemberModal, setShowAddMemberModal] = useState(false);
    const [searchTerm, setSearchTerm] = useState(''); // State for search term
    const [selectedDomain, setSelectedDomain] = useState('');
    const [showConfirmationPopup, setShowConfirmationPopup] = useState(false); // State for confirmation popup
    const [selectedParticipant, setSelectedParticipant] = useState(null);
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [rescheduleSession, setRescheduleSession] = useState(null);
    const [rescheduleEmp, setRescheduleEmp] = useState(null);
    const [rescheduleSlots, setRescheduleSlots] = useState(null);
    const [isEditMode, setIsEditMode] = useState(false);
    const [totalCount, setTotalCount] = useState(0);
    const [limit, setLimit] = useState(10);
    const [offset, setOffset] = useState(0);
    const [sort, setSort] = useState('emp_name'); // default sort field
    const [sortDirection, setSortDirection] = useState('asc'); // or 'desc'

    const navigate = useNavigate();

    const location = useLocation(); // Get location to access state
    const { seatAvailable, registeredCount } = location.state || {};
    const empId = encryptStorage.getItem('employeeId')

    const fetchParticipants = async (
        domain = selectedDomain,
        search = searchTerm,
        currentOffset = offset,
        currentLimit = limit,
        currentSort = sort,
        currentSortDirection = sortDirection,
        append = false // for "Show More"
    ) => {
        setLoading(true);
        try {
            // Compose sort param for API
            let sortParam = currentSortDirection === 'desc' ? `-${currentSort}` : currentSort;

            const response = await GetParticipants(
                sessionId,
                slotId,
                domain,
                search,
                sortParam,
                currentLimit,
                currentOffset
            );
            if (response.data.code === 200) {
                const data = response.data.data.data;
                if (append) {
                    setParticipants(prev => ({
                        ...prev,
                        participants: [...prev.participants, ...data.participants],
                        total_count: data.total_count || prev.total_count,
                    }));
                } else {
                    setParticipants(data);
                }
                setTotalCount(data.total_count || 0);
                setSessionInfo(prev => ({
                    ...prev,
                    registeredCount: data.total_count || 0,
                    seatAvailable: data.seat_available || prev.seatAvailable || location.state?.seatAvailable || 0,
                }));
                setLoading(false);
            } else {
                setError('Failed to fetch participants');
            }
        } catch (error) {
            setError('Error fetching participants');
        } finally {
            setLoading(false);
        }
    };


    const fetchDomains = async () => {
        try {
            const response = await GetDomain(); // Call API to get domains
            if (response.data.code === 200) {
                setDomains(response.data.data);
                // Assuming domains are in response.data.data
            } else {
                setError('Failed to fetch domains');
            }
        } catch (error) {
            console.error('Error fetching domains:', error);
            setError('Error fetching domains');
        }
    };

    useEffect(() => {
        const fetchSessionDetails = async () => {
            try {
                const response = await GetSessions(empId);
                const session = response.data.data.sessions.find(s => s.session_id === sessionId);
                const slot = session?.slots.find(s => s.slot_id === slotId);
                setSessionInfo({
                    seatAvailable: slot?.seat_available || location.state?.seatAvailable || 0,
                    registeredCount: participants.total_count || 0,
                    sessionGrade: session.grade
                });
            } catch (error) {
                console.error('Error fetching session details:', error);
            }
        };

        fetchDomains();
        fetchSessionDetails();
        setOffset(0);
        fetchParticipants(selectedDomain, searchTerm, 0, limit, sort, sortDirection, false);
    }, [sessionId, slotId, selectedDomain, searchTerm, sort, sortDirection]);

    // When filter/search/sort changes
    useEffect(() => {
        fetchDomains();
        setOffset(0);
        fetchParticipants(selectedDomain, searchTerm, 0, limit, sort, sortDirection, false);
    }, [sessionId, slotId, selectedDomain, searchTerm, sort, sortDirection]);

    // When "Show More" is clicked
    const handleShowMore = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        fetchParticipants(selectedDomain, searchTerm, newOffset, limit, sort, sortDirection, true);
    };

    const handleMemberAdded = () => {
        fetchParticipants(selectedDomain, searchTerm); 
        setShowAddMemberModal(false); 
    };

    const handleRemoveParticipant = (participant) => {
        setSelectedParticipant(participant);
        setShowConfirmationPopup(true);
    };

    const handleSort = (field) => {
        if (sort === field) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSort(field);
            setSortDirection('asc');
        }
    };

    const confirmRemoval = async () => {
        try {
            // Replace this with your actual API call
            const payload = {
                participant_id: selectedParticipant.participant_id,
                session_id: sessionId,
                slot_id: slotId,
                emp_id: selectedParticipant.emp_id,
                status: "Remove"
            };
            const response = await PostRegistration(payload);
            if (response.data.code === 200) {
                Toast.success("Member removed successfully");
                setShowConfirmationPopup(false);
                setSelectedParticipant(null);
                // Refresh the participants list
                fetchParticipants(selectedDomain, searchTerm);
            } else {
                Toast.error("Failed to remove member");
                setShowConfirmationPopup(false);
                setSelectedParticipant(null);
                fetchParticipants(selectedDomain, searchTerm);
            }
        } catch (error) {
            Toast.error("Failed to remove member");
            setShowConfirmationPopup(false);
            setSelectedParticipant(null);
            fetchParticipants(selectedDomain, searchTerm);
        }
    };

    // Function to format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        const [year, month, day] = dateString.split('-').map(Number);
        const date = new Date(Date.UTC(year, month - 1, day));
        const options = { day: '2-digit', month: 'short', year: 'numeric', timeZone: 'UTC' };
        return date.toLocaleDateString('en-GB', options).replace(/ /g, ' ');
    };

    const handleEditParticipant = async (participant) => {

        try {
            // Fetch all slots for this employee
            const response = await GetSessions(participant.emp_id);
            if (response.data.code === 200) {
                const fetchedSlots = response.data.data.sessions.find(s => s.session_id)?.slots || [];
                setRescheduleSlots(fetchedSlots); 
                setRescheduleEmp(participant);
                setShowRescheduleModal(true);
            } else {
                Toast.error('Failed to fetch session slots for rescheduling');
            }
        } catch (err) {
            Toast.error('Error fetching session slots');
        }

        setIsEditMode(true);
    };

    return (
        <div className="body-bg">
            {/* <Loader isLoading={loading} /> */}

            <div className="container-fluid mt-6">
                <div className="row justify-content-center my-3">
                    <Toaster />
                    <div className="col-md-11 d-flex align-items-center">
                        {/* Page Header Starts Here */}
                        <div className="d-flex justify-content-between align-items-center">
                            <img
                                src="/Images/back-arrow.svg"
                                alt="back-arrow"
                                className="me-3"
                                onClick={() => navigate('/Session')}
                                style={{ cursor: 'pointer' }}
                            />
                            <h4 className="font-18 bold-text mb-0">{participants.session_name}</h4>
                            <span className="session-details-badge font-11 semibold-text text-white px-3 py-1 ms-2">
                                {participants.session_date ? formatDate(participants.session_date) : ''} ({participants.start_time} - {participants.end_time})
                            </span>
                        </div>
                        {/* Page Header Ends Here */}

                        <div className="ms-auto d-flex">
                            <div className="table-filter input-group me-3">
                                <label className="input-group-text font-13 medium-text" htmlFor="inputGroupSelect01">Practice</label>
                                <select className="form-select font-13 medium-text" id="inputGroupSelect01" value={selectedDomain} onChange={(e) => setSelectedDomain(e.target.value)} onBlur={() => fetchParticipants(selectedDomain, searchTerm)} onClick={() => fetchParticipants(selectedDomain, searchTerm)}>
                                    <option selected value="">Select</option>
                                    {domains.map(domain => (
                                        <option key={domain.id} value={domain.domain}>{domain.domain}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="input-group flex-nowrap search-group me-3">
                                <input type="search" className="form-control search-align" placeholder="Search" aria-label="Username" aria-describedby="addon-wrapping" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} // Update search term
                                    onBlur={() => fetchParticipants(selectedDomain, searchTerm)} onClick={() => fetchParticipants(selectedDomain, searchTerm)} />
                                <button className="input-group-text btn btn-search-custom" id="addon-wrapping" onClick={() => fetchParticipants(selectedDomain, searchTerm)}>
                                    <img src="/Images/search-icon.svg" alt="search-icon" />
                                </button>
                            </div>

                            <button className="btn hive-btn-primary text-nowrap" onClick={() => setShowAddMemberModal(true)} disabled={sessionInfo.registeredCount >= sessionInfo.seatAvailable}>
                                <img src="/Images/hive-add-icon.svg" className="me-2" alt="add-icon" />Add Member
                            </button>
                        </div>
                    </div>

                    <div className="col-md-11 mt-4">
                        {loading ? (
                            <p>Loading participants...</p>
                        ) : error ? (
                            <p>{error}</p>
                        ) : participants.participants.length === 0 ? (
                            // Render empty state if participants array is empty
                            <div className="row">
                                <div className="d-flex flex-column align-items-center justify-content-center container-height">
                                    <img className="empty-img-width mb-4" src="/Images/table-empty-state.svg" alt="empty-state" />
                                    <p className="font-14 medium-text grey-text mb-1">No participants registered yet</p>
                                </div>
                            </div>
                        ) : (
                            <table className="table bg-white hive-grid mb-3 table-borderless">
                                <thead className="hive-table-head">
                                    <tr>
                                        <th className="bold-text font-12" onClick={() => handleSort('userid')}>
                                            Employee ID
                                            <span className="ps-2">
                                                <img src={sort === 'userid' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                            </span>
                                        </th>
                                        <th className="bold-text font-12" onClick={() => handleSort('emp_name')}>
                                            Employee Name
                                            <span className="ps-2">
                                                <img src={sort === 'emp_name' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                            </span>
                                        </th>
                                        <th className="bold-text font-12" onClick={() => handleSort('domain')}>
                                            Practice
                                            <span className="ps-2">
                                                <img src={sort === 'domain' && sortDirection === 'asc' ? "/Images/Sort-up.svg" : "/Images/sort-icon.svg"} alt="Sorticon" className="hive-table-sort" />
                                            </span>
                                        </th>
                                        <th className="text-center bold-text font-12">Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {participants.participants.map(participant => (
                                        <tr key={participant.participant_id} className="hive-table-data regular-text align-middle">
                                            <td>{participant.userid}</td>
                                            <td>
                                                <div className="d-flex align-items-center">
                                                    {/* Replace with actual profile image if available */}
                                                    <img src="/Images/logo-person-user-person-icon.jpg" alt="profile-img" className="table-image" />
                                                    <div className="ms-2">
                                                        <p className="text-dark mb-0">{participant.emp_name}</p>
                                                        <p className="text-secondary font-11 mb-0">{participant.role}</p>
                                                    </div>
                                                </div>
                                            </td>
                                            <td>{participant.domain}</td>
                                            <td className="text-center">
                                                {/* Add action buttons if needed */}
                                                <button className="bg-transparent border-0"><img src="/Images/edit-action.svg" alt="edit" onClick={() => handleEditParticipant(participant)} /></button>
                                                <button className="bg-transparent border-0"><img src="/Images/remove-icon.svg" alt="delete" onClick={() => handleRemoveParticipant(participant)} /></button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}

                        {/* Pagination or Show More button can be added here if needed */}
                        <p className="font-12 pagination">
                            Showing <span className="bold-text mx-1">{participants.participants.length}</span> of
                            <span className="bold-text mx-1">{participants.total_count}</span> Records
                        </p>
                        {participants.participants.length < participants.total_count && (
                            <div className="text-center">
                                <button className="btn show-more-btn" onClick={handleShowMore}>Show More</button>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Include Add Member Modal */}
            {showAddMemberModal && (
                <AddMemberModal
                    sessionId={sessionId}
                    slotId={slotId}
                    onClose={handleMemberAdded}
                    onToast={Toast.success}
                    sessionGrade={sessionInfo.sessionGrade}
                />
            )}

            {/* Include Confirmation Popup */}
            {showConfirmationPopup && selectedParticipant && (
                <ConfirmationPopup
                    open={showConfirmationPopup}
                    message={`Are you sure you want to remove ${selectedParticipant.emp_name} from this session?`}
                    onClose={() => setShowConfirmationPopup(false)}
                    onConfirm={confirmRemoval}
                />
            )}

            {/* Include Edit Participants Modal */}
            {showRescheduleModal && rescheduleSlots && rescheduleEmp && (
                <EditParticipants
                    participant={rescheduleEmp} 
                    availableSlots={rescheduleSlots} 
                    empId={rescheduleEmp.emp_id} 
                    fetchSessions={fetchParticipants} 
                    onClose={() => setShowRescheduleModal(false)} 
                    sessionId={sessionId}
                />
            )}
        </div>
    );
};

export default SessionParticipants;
